import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, User, Phone, MapPin, Calendar, Eye, Loader } from 'lucide-react';
import apiService from '../services/api';

const PatientSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadRecentPatients();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        performSearch(searchTerm);
      } else {
        setFilteredPatients(patients);
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, patients]);

  const loadRecentPatients = async () => {
    try {
      setLoading(true);
      setError(null);
    
      const recentPatients = await apiService.getRecentPatients();
      const patientsArray = Array.isArray(recentPatients) ? recentPatients : [];
    
      setPatients(patientsArray);
      setFilteredPatients(patientsArray);
    } catch (err) {
      console.error('Failed to load patients:', err);
      setError('Failed to load patients');
      setPatients([]);
      setFilteredPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const performSearch = async (term) => {
    try {
      setSearching(true);
      setError(null);
    
      const searchResults = await apiService.searchPatients(term);
      const resultsArray = Array.isArray(searchResults) ? searchResults : [];
    
      setFilteredPatients(resultsArray);
    } catch (err) {
      console.error('Search failed:', err);
      setError('Search failed. Please try again.');
      setFilteredPatients([]);
    } finally {
      setSearching(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  
    if (value.trim()) {
      setSearching(true);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setFilteredPatients(patients);
    setSearching(false);
  };

  const PatientCard = ({ patient }) => (
    <div className="card patient-card">
      <div className="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-4">
        {/* Patient Photo */}
        <div className="flex-shrink-0 self-center sm:self-start">
          {patient.photo ? (
            <img 
              src={`https://api.novadent.site/uploads/patients/${patient.photo}`}
              alt={patient.full_name}
              className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-blue-600 text-white flex items-center justify-center text-xl font-bold">
              {patient.full_name?.charAt(0) || 'P'}
            </div>
          )}
        </div>

        {/* Patient Info */}
        <div className="flex-grow">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-3 sm:space-y-0">
            <div className="text-center sm:text-left">
              <h3 className="text-lg font-semibold text-gray-900">{patient.full_name}</h3>
              <p className="text-sm text-gray-600 font-medium">ID: {patient.patient_id}</p>
            </div>
            <Link 
              to={`/patient/${patient.id}`}
              className="btn-primary flex items-center justify-center space-x-1 text-sm w-full sm:w-auto"
            >
              <Eye className="h-4 w-4" />
              <span>View Profile</span>
            </Link>
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 text-sm">
            <div className="flex items-center text-gray-600 justify-center sm:justify-start">
              <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="truncate">{patient.phone}</span>
            </div>
            <div className="flex items-center text-gray-600 justify-center sm:justify-start">
              <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>{patient.age} years old, {patient.gender}</span>
            </div>
            <div className="flex items-center text-gray-600 justify-center sm:justify-start sm:col-span-2 lg:col-span-1">
              <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="truncate">{patient.address || 'No address provided'}</span>
            </div>
          </div>

          {/* Medical Alerts - Mobile Optimized */}
          {(patient.allergies || patient.chronic_conditions) && (
            <div className="mt-3 flex flex-wrap gap-2 justify-center sm:justify-start">
              {patient.allergies && patient.allergies !== 'None' && (
                <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                  Allergies: {patient.allergies}
                </span>
              )}
              {patient.chronic_conditions && patient.chronic_conditions !== 'None' && (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                  Conditions: {patient.chronic_conditions}
                </span>
              )}
            </div>
          )}

          {/* Visit Summary */}
          <div className="mt-3 flex flex-col sm:flex-row sm:justify-between sm:items-center text-sm text-gray-600 space-y-1 sm:space-y-0 text-center sm:text-left">
            <span>Total Visits: {patient.total_visits || 0}</span>
            {patient.last_visit_date && (
              <span>Last Visit: {new Date(patient.last_visit_date).toLocaleDateString()}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6 px-4 sm:px-0">
        <div className="flex items-center justify-center py-12">
          <Loader className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading patients...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 px-4 sm:px-0">
      {/* Header - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center justify-center sm:justify-start">
          <Search className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 mr-2" />
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Find Patient</h1>
        </div>
        <Link to="/register" className="btn-primary w-full sm:w-auto text-center">
          Add New Patient
        </Link>
      </div>

      {/* Search Bar */}
      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, phone number, or patient ID..."
            value={searchTerm}
            onChange={handleInputChange}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-lg"
          />
          {searching && (
            <Loader className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 animate-spin text-gray-400" />
          )}
        </div>
      
        {searchTerm && (
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 text-sm text-gray-600">
            <span>
              {filteredPatients.length} patient{filteredPatients.length !== 1 ? 's' : ''} found
              {searchTerm && ` for "${searchTerm}"`}
            </span>
            {searchTerm && (
              <button 
                onClick={clearSearch}
                className="text-blue-600 hover:text-blue-800 text-center sm:text-right"
              >
                Clear search
              </button>
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="card bg-red-50 border-red-200">
          <div className="flex items-center">
            <div className="text-red-600 mr-3">⚠️</div>
            <div>
              <p className="text-red-800 font-medium text-sm sm:text-base">{error}</p>
              <button 
                onClick={loadRecentPatients}
                className="text-red-600 hover:text-red-800 text-sm underline mt-1"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search Results */}
      <div className="space-y-4">
        {filteredPatients.length > 0 ? (
          filteredPatients.map(patient => (
            <PatientCard key={patient.id} patient={patient} />
          ))
        ) : searchTerm ? (
          <div className="card text-center py-8">
            <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No patients found</h3>
            <p className="text-gray-600 mb-4 text-sm sm:text-base">
              No patients match your search for "{searchTerm}"
            </p>
            <Link to="/register" className="btn-primary">
              Register New Patient
            </Link>
          </div>
        ) : patients.length === 0 ? (
          <div className="card text-center py-8">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No patients registered</h3>
            <p className="text-gray-600 mb-4 text-sm sm:text-base">
              Get started by registering your first patient
            </p>
            <Link to="/register" className="btn-primary">
              Register First Patient
            </Link>
          </div>
        ) : null}
      </div>

      {/* Quick Stats - Mobile Optimized */}
      {patients.length > 0 && (
        <div className="card bg-blue-50">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center sm:text-left">Quick Statistics</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-blue-600">{patients.length}</p>
              <p className="text-sm text-gray-600">Recent Patients</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">
                {patients.reduce((sum, p) => sum + (parseInt(p.total_visits) || 0), 0)}
              </p>
              <p className="text-sm text-gray-600">Total Visits</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">
                {patients.filter(p => p.last_visit_date).length}
              </p>
              <p className="text-sm text-gray-600">Active Patients</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientSearch;